// Rig Data Sheet — pump-derived calculations.
//
// Pure module. Wraps the trusted v1 helper computePumpDisplacement (in
// /lib/calc/pumpDisplacement.js) — does NOT reimplement physics. The
// only addition here is:
//   (a) v2 pump-type label mapping ("triplex"|"duplex" → v1 enums)
//   (b) cross-unit conversion (always returns BOTH bbl/stk and m³/stk
//       regardless of the rig's primary unit system)
//   (c) strokes-per-bbl and strokes-per-m³ inverses
//
// All inputs may be strings (form-edited). Missing/invalid inputs return
// null, never throw.

import { computePumpDisplacement } from "@/lib/calc/pumpDisplacement";

// 1 US oil barrel = 0.158987294928 m³ (exact API standard).
export const BBL_TO_M3 = 0.158987294928;
export const M3_TO_BBL = 1 / BBL_TO_M3;

// v2 UI label → v1 internal enum used by computePumpDisplacement.
const PUMP_TYPE_V2_TO_V1 = {
  triplex: "triplex_sa",  // single-acting triplex (IADC convention)
  duplex:  "duplex_da",   // double-acting duplex (IADC convention)
};

/**
 * Compute all four derived values for a v2 Rig-Sheet pump.
 *
 * @param {object} pump   v2 pump shape: { pumpType, linerSize, strokeLength,
 *                                         rodDiameter, efficiency }
 * @param {string} units  rig's primary unit system: "IMP" or "SI" — controls
 *                        which dimension family the geometry inputs are in.
 * @returns {{ dispBblPerStk:string, dispM3PerStk:string,
 *             strokesPerBbl:string, strokesPerM3:string } | null}
 *          Strings (form-ready, fixed precision) or null if not computable.
 */
export function computePumpDerived(pump, units) {
  if (!pump || typeof pump !== "object") return null;
  const v1Type = PUMP_TYPE_V2_TO_V1[pump.pumpType];
  if (!v1Type) return null;
  const v1pump = {
    pumpType:   v1Type,
    liner:      pump.linerSize,
    stroke:     pump.strokeLength,
    rodDia:     pump.rodDiameter,
    efficiency: pump.efficiency,
  };
  const disp = computePumpDisplacement(v1pump, units);
  if (disp == null || !Number.isFinite(disp) || disp <= 0) return null;

  // computePumpDisplacement returns bbl/stk when units==="IMP", m³/stk when "SI".
  let dispBblPerStk, dispM3PerStk;
  if (units === "SI") {
    dispM3PerStk  = disp;
    dispBblPerStk = disp * M3_TO_BBL;
  } else {
    dispBblPerStk = disp;
    dispM3PerStk  = disp * BBL_TO_M3;
  }
  const strokesPerBbl = 1 / dispBblPerStk;
  const strokesPerM3  = 1 / dispM3PerStk;

  return {
    dispBblPerStk: dispBblPerStk.toFixed(4),
    dispM3PerStk:  dispM3PerStk.toFixed(6),
    strokesPerBbl: strokesPerBbl.toFixed(2),
    strokesPerM3:  strokesPerM3.toFixed(2),
  };
}

/**
 * Run computePumpDerived on every pump in a RigSheet payload and update
 * each pump's `computed` field in-place. Returns the same payload for
 * chaining. Pure on inputs (creates a new top-level array but does not
 * touch other sections).
 */
export function recomputeAllPumps(payload) {
  if (!payload || !Array.isArray(payload.pumps)) return payload;
  const units = payload.units || "IMP";
  return {
    ...payload,
    pumps: payload.pumps.map((p) => {
      const c = computePumpDerived(p, units);
      return {
        ...p,
        computed: c || { dispBblPerStk: "", dispM3PerStk: "", strokesPerBbl: "", strokesPerM3: "" },
      };
    }),
  };
}
